import _ from 'lodash';

import { generatePositionForGridControl, getPositions } from './../position.utils';
import { WARNING_SOURCES } from '../../../constants/WarningsConstants';
import { isNilOrEmpty } from '../../../utils/ramdaUtils';
import {
  MOVE_BUTTON_TYPES,
  NOT_BOUND_CONTROL_TYPES,
  RULE_EDITOR_PROPS,
} from '../../constants/editor.constants';
import {
  BUTTON_TYPES,
  CONTAINER_CONTROLS,
  SS_CONTROL_TYPE,
  ZOOM_SOURCE_TYPE,
} from '../../constants/form-manager.constants';
import { findParentByType, getLastModifiedDateContainer } from '../editor.utils';

export const getLastControlIdInGrid = ({ formModel, gridId }) => {
  let prevControlId = '';
  const { childrenNodes = [] } = formModel[gridId];

  const columnIds = childrenNodes
    .filter((child) => formModel[child]?.controlType === SS_CONTROL_TYPE.GRID_COLUMN)
    .reverse();

  _.forEach(columnIds, (columnId) => {
    const { childrenNodes: columnControlIds = [] } = formModel[columnId];
    const lastControlId = columnControlIds.length && columnControlIds[columnControlIds.length - 1];
    const lastControl = formModel[lastControlId];

    if (lastControl?.controlType === SS_CONTROL_TYPE.ADVANCED_TEXT_BOX) {
      const { childrenNodes: advancedTextBoxControlIds = [] } = lastControl;
      const lastControlInAdvancedTextBox =
        advancedTextBoxControlIds.length &&
        advancedTextBoxControlIds[advancedTextBoxControlIds.length - 1];
      if (lastControlInAdvancedTextBox) {
        prevControlId = lastControlInAdvancedTextBox;
        return false;
      }
    } else if (lastControl) {
      prevControlId = lastControlId;
      return false;
    }
  });

  return prevControlId;
};

export const getElementProperties = (element) => {
  if (_.isEmpty(element)) {
    return {};
  }
  const result = {};
  const ignoredProps = [
    'controlId',
    'controlType',
    'parent',
    'defaultLabel',
    'childrenNodes',
    'zoomSource',
  ];

  //we shouldn't send entityName and fieldName while adding controls that not bound to DataModel
  //it causes api call failing on save&publish
  if (NOT_BOUND_CONTROL_TYPES.includes(element?.controlType)) {
    ignoredProps.push('entityName', 'fieldName', 'defaultLabel');
  }

  Object.keys(element).forEach((key) => {
    if (!ignoredProps.includes(key)) {
      result[key] = element[key];
    }
  }, {});
  return result;
};

export const getControlChildrenNodes = (
  formModel,
  currentControl,
  containerFormName,
  formLastModified,
  res = []
) => {
  const callBack = (childId) => {
    const { controlId = '', childrenNodes = [] } = formModel[childId] || [];

    if (!controlId.includes(SS_CONTROL_TYPE.ADVANCED_TEXT_BOX)) {
      res.push({ controlId, containerFormName, formLastModified });
    }

    childrenNodes.length && childrenNodes.forEach(callBack);
  };

  formModel[currentControl].childrenNodes.forEach(callBack);

  return res;
};

export const getPrevControlId = (formModel, controlParent, controlId) => {
  const control = formModel[controlId];
  if (control?.controlType === SS_CONTROL_TYPE.SUB_GRID) {
    return getLastControlIdInGrid({
      formModel,
      gridId: controlParent.controlId,
    });
  }

  const currentIndex = controlParent?.childrenNodes.indexOf(controlId);
  const prevControlId = currentIndex === 0 ? '' : controlParent?.childrenNodes[currentIndex - 1];

  const previousControl = formModel[prevControlId];

  //get prevControl id for new zoom or textView when prevControl is zoom with zoomType "entity"
  if (
    previousControl?.controlType === SS_CONTROL_TYPE.ZOOM &&
    previousControl?.zoomSource === ZOOM_SOURCE_TYPE.ENTITY &&
    controlParent?.controlType === SS_CONTROL_TYPE.ADVANCED_TEXT_BOX
  ) {
    return controlParent.childrenNodes[currentIndex - 2] ?? '';
  }

  //get prevControl id for new element when prevControl is advancedTextBox
  if (previousControl?.controlType === SS_CONTROL_TYPE.ADVANCED_TEXT_BOX) {
    const lastAdvancedTextBoxElementId =
      previousControl.childrenNodes[previousControl.childrenNodes.length - 1];

    const lastAdvancedTextBoxElement = formModel[lastAdvancedTextBoxElementId];

    if (
      lastAdvancedTextBoxElement?.controlType === SS_CONTROL_TYPE.ZOOM &&
      lastAdvancedTextBoxElement?.zoomSource === ZOOM_SOURCE_TYPE.ENTITY
    ) {
      return previousControl.childrenNodes[previousControl.childrenNodes.length - 2];
    }

    return lastAdvancedTextBoxElementId;
  }

  //get prevControl id for new textBox
  if (
    controlParent?.controlType === SS_CONTROL_TYPE.ADVANCED_TEXT_BOX &&
    control.controlType === SS_CONTROL_TYPE.TEXT_BOX
  ) {
    const indexForParentControl = formModel[controlParent.parent].childrenNodes.indexOf(
      controlParent.controlId
    );
    const newPrevControlId =
      indexForParentControl === 0
        ? ''
        : formModel[controlParent.parent].childrenNodes[indexForParentControl - 1];

    if (formModel[newPrevControlId]?.controlType === SS_CONTROL_TYPE.ADVANCED_TEXT_BOX) {
      const lastAdvancedTextBoxElement =
        formModel[
          formModel[newPrevControlId].childrenNodes[
            formModel[newPrevControlId].childrenNodes.length - 1
          ]
        ];

      if (
        lastAdvancedTextBoxElement?.controlType === SS_CONTROL_TYPE.ZOOM &&
        lastAdvancedTextBoxElement?.zoomSource === ZOOM_SOURCE_TYPE.ENTITY
      ) {
        return formModel[newPrevControlId].childrenNodes[
          formModel[newPrevControlId].childrenNodes.length - 2
        ];
      }

      return formModel[newPrevControlId].childrenNodes[
        formModel[newPrevControlId].childrenNodes.length - 1
      ];
    }

    return newPrevControlId;
  }

  return prevControlId;
};

export const getAddedElementsArrayFormModel = (AddedFieldsFormModel) =>
  AddedFieldsFormModel.reduce((acc, current) => {
    const { controlId, saved } = current;
    if (!saved) {
      acc.push(controlId);
    }
    return acc;
  }, []);

export const getDeletedElementsArrayFormModel = (
  DeletedFieldsFormModel,
  addedElementsFormModelArray
) =>
  DeletedFieldsFormModel.reduce((acc, current) => {
    const { controlId } = current || [];
    if (!addedElementsFormModelArray.includes(controlId)) {
      acc.push(controlId);
    }
    return acc;
  }, []);

export const makeDiffForPayloadBuilder = (tempDiff, formModel, formInfo) =>
  Object.keys(tempDiff).reduce((acc, key) => {
    const {
      update,
      interaction,
      validation,
      dataMappingList,
      sourceFields,
      extraPrompts,
    } = tempDiff[key];

    const containerFormName = findParentByType(formModel, key, formInfo, SS_CONTROL_TYPE.SUB_FORM);

    if (tempDiff[key].dataSourceFilter) {
      tempDiff[key].dataSourceFilter = formModel[key].dataSourceFilter;
    }
    if (tempDiff[key].paramList) {
      tempDiff[key].paramList = formModel[key].paramList;
    }
    if (tempDiff[key].chartData) {
      tempDiff[key].chartData = formModel[key].chartData;
    }

    const newKey = key.includes('formBox_') ? 'formBox' : key;

    const ruleEditorPropsDiffValues = RULE_EDITOR_PROPS.reduce((acc, propName) => {
      const propValue = tempDiff[key][propName];

      if (!propValue) {
        return acc;
      }

      return { ...acc, [propName]: formModel[key][propName] };
    }, {});

    const newDiff = {
      [newKey]: {
        ...tempDiff[key],
        containerFormName,
        formLastModified: getLastModifiedDateContainer(formModel, containerFormName, formInfo),
        ...(update && { update: update.rules ? formModel[key].update : '' }),
        ...(interaction && { interaction: interaction.rules ? formModel[key].interaction : '' }),
        ...(validation && { validation: validation.rules ? formModel[key].validation : '' }),
        ...(dataMappingList && {
          dataMappingList: dataMappingList.length ? formModel[key].dataMappingList : '',
        }),
        ...(sourceFields && { sourceFields: formModel[key].sourceFields }),
        ...(extraPrompts && { extraPrompts: formModel[key].extraPrompts }),
        ...ruleEditorPropsDiffValues,
      },
    };

    acc.push(newDiff);

    return acc;
  }, []);

export const diffPayloadBuilder = (diff, AddedFieldsFormModel) => {
  return diff.filter((item) => !AddedFieldsFormModel.includes(Object.keys(item)[0]));
};

export const addedImportElementsPayloadBuilder = (
  formModel,
  formInfo,
  AddedFields,
  DeletedFields
) => {
  const fields = Object.keys(formModel).reduce((acc, cur) => {
    const obj = {
      ...formModel[cur],
      childrenNodes: formModel[cur]?.childrenNodes || [],
    };

    acc.push(obj);
    return acc;
  }, []);

  const rootContainerFormName = formInfo.formKey;

  const positions = getPositions(formModel);
  const payloadElements = fields
    .filter(
      (ele) =>
        ele.controlType !== SS_CONTROL_TYPE.ADVANCED_TEXT_BOX &&
        ele.controlType !== SS_CONTROL_TYPE.FORM
    )
    .reduce((acc, current) => {
      const { controlId = '', parent = '', controlType = '', defaultLabel = '' } = current;

      const parentControlId = parent.includes('ssAdvancedTextBox')
        ? formModel[parent].parent
        : parent;

      const containerFormName = findParentByType(formModel, controlId, formInfo, 'ssSubForm');
      const formLastModified = getLastModifiedDateContainer(formModel, containerFormName, formInfo);
      const nodeElement = formModel[controlId];

      const elementProperties = getElementProperties(nodeElement);

      const element = {
        controlId,
        parentControlId,
        previousControlId: getPrevControlId(formModel, formModel[parent], controlId),
        controlType,
        defaultLabel,
        containerFormName,
        formLastModified,
        position: positions[controlId],
        properties: elementProperties,
      };

      if (
        formModel[parentControlId]?.controlType === SS_CONTROL_TYPE.FORM &&
        formModel[parentControlId]?.parent !== 'root'
      ) {
        element.parentControlId = formModel[parentControlId].parent;
      }

      if (formModel[parentControlId]?.controlType === SS_CONTROL_TYPE.GRID_COLUMN) {
        const gridControlPosition = generatePositionForGridControl(
          formModel,
          element,
          parentControlId
        );
        element.generateColumn = gridControlPosition === 0;
      }

      if (controlType === SS_CONTROL_TYPE.ZOOM) {
        element.defaultLabel = undefined;
      }

      const isAdded = AddedFields.find((f) => f.controlId === element.controlId);
      const isDeleted = DeletedFields.find((f) => f.controlId === element.controlId);
      const isSaveOfflineButton = element.properties?.buttonType === BUTTON_TYPES.SAVE_OFFLINE;
      const isRootFormElement = element.containerFormName === rootContainerFormName;

      if (!isDeleted && ((!isSaveOfflineButton && isRootFormElement) || isAdded)) {
        acc.push(element);
      }

      return acc;
    }, []);

  return payloadElements.sort((a, b) => a.position - b.position);
};

export const addedElementsPayloadBuilder = (AddedFields, formModel, formInfo) => {
  const positions = getPositions(formModel);
  const newPayloadAddElements = AddedFields.filter(
    (ele) =>
      !ele.saved &&
      ele.controlType !== SS_CONTROL_TYPE.ADVANCED_TEXT_BOX &&
      !(formModel[ele.controlId]?.isNewAutogeneratedZoom === true)
  ).reduce((acc, current) => {
    const { controlId = '', parent = '', controlType = '', defaultLabel = '' } = current;

    const parentControlId = parent.includes('ssAdvancedTextBox')
      ? formModel[parent].parent
      : parent;

    const containerFormName = findParentByType(formModel, controlId, formInfo, 'ssSubForm');
    const formLastModified = getLastModifiedDateContainer(formModel, containerFormName, formInfo);
    const nodeElement = formModel[controlId];

    const elementProperties = getElementProperties(nodeElement);

    const newElement = {
      controlId,
      parentControlId,
      previousControlId: getPrevControlId(formModel, formModel[parent], controlId),
      controlType,
      defaultLabel,
      containerFormName,
      formLastModified,
      position: positions[controlId],
      properties: elementProperties,
    };

    if (formModel[parentControlId].controlType === SS_CONTROL_TYPE.GRID_COLUMN) {
      const gridControlPosition = generatePositionForGridControl(
        formModel,
        newElement,
        parentControlId
      );
      newElement.generateColumn = gridControlPosition === 0;
    }

    if (controlType === SS_CONTROL_TYPE.ZOOM) {
      newElement.defaultLabel = undefined;
    }

    acc.push(newElement);
    return acc;
  }, []);

  return newPayloadAddElements.sort((a, b) => a.position - b.position);
};

export const deletedElementsPayloadBuilder = (deletedElements, formModel) => {
  const newPayloadDeleteElements = deletedElements.reduce((acc, current) => {
    const { controlId = '', containerFormName = '', formLastModified = '', childrenNodes = [] } =
      current || [];

    const deletedElement = {
      controlId,
      containerFormName,
      formLastModified,
    };

    if (!controlId.includes(SS_CONTROL_TYPE.ADVANCED_TEXT_BOX)) {
      acc.push(deletedElement);
    }

    if (childrenNodes.length) {
      acc = acc.concat(
        getControlChildrenNodes(formModel, controlId, containerFormName, formLastModified)
      );
    }

    return acc;
  }, []);

  return newPayloadDeleteElements;
};

export const movedElementsPayloadBuilder = (MovedFields, addedFields, formModel, formInfo) => {
  // remove repeating movements and added elements
  const addedFieldsIds = addedFields.map((field) => field.controlId);
  const filteredMovedFields = MovedFields.filter(
    (field) => !addedFieldsIds.includes(field?.controlId)
  ).reduceRight((fields, field) => {
    const isAlreadyMoved = fields.some(({ controlId }) => controlId === field.controlId);

    if (!isAlreadyMoved) {
      fields.push(field);
    }
    return fields;
  }, []);

  const allChildrenIds = _.chain(formModel)
    .values()
    .map('childrenNodes')
    .flatten()
    .filter()
    .value();

  const allChildrenIdsMap = {};

  allChildrenIds.forEach((id, index) => {
    allChildrenIdsMap[id] = index;
  });

  // sort movements by the view on the ui
  filteredMovedFields.sort(
    (a, b) => allChildrenIdsMap[a.controlId] - allChildrenIdsMap[b.controlId]
  );

  return filteredMovedFields.reduce((acc, current) => {
    const { controlId = '', parentControlId = '' } = current;

    const parentControl = formModel[parentControlId];
    const control = formModel[controlId];

    if (!control) {
      return acc;
    }

    const containerFormName = findParentByType(
      formModel,
      controlId,
      formInfo,
      SS_CONTROL_TYPE.SUB_FORM
    );

    const newControlId = controlId.includes(SS_CONTROL_TYPE.ADVANCED_TEXT_BOX)
      ? control.childrenNodes[0]
      : controlId;

    const newMovedFiledPayload = {
      controlId: newControlId,
      parentControlId:
        parentControl?.controlType === SS_CONTROL_TYPE.FORM ? 'formBox' : parentControlId,
      prevControlId: getPrevControlId(formModel, parentControl, controlId),
      formLastModified: getLastModifiedDateContainer(formModel, containerFormName, formInfo),
      containerFormName,
    };
    acc.push(newMovedFiledPayload);

    return acc;
  }, []);
};

export const getLastControlIdInAdvancedTextBox = ({ controlParentId, formModel }) => {
  const childrenNodes = formModel[controlParentId].childrenNodes;
  return childrenNodes[childrenNodes.length - 1];
};

export const getPreviousControlIdForNewZoom = ({ controlParentId, formModel }) => {
  const childrenNodes = formModel[controlParentId]?.childrenNodes ?? [];

  const zooms = childrenNodes.filter(
    (nodeId) => formModel[nodeId].controlType === SS_CONTROL_TYPE.ZOOM
  );

  if (!zooms?.length) {
    return childrenNodes[0];
  }

  return zooms[zooms.length - 1];
};

export const validateZoomControls = (element) => {
  const errors = [];
  if (element.controlType === SS_CONTROL_TYPE.ZOOM) {
    if (element.dataObject === '') {
      errors.push({
        instanceId: element.controlId,
        message: 'Source Identifier is required!',
        source: WARNING_SOURCES.EDITOR,
      });
    }
    if (element.dataLookUpType === '') {
      errors.push({
        instanceId: element.controlId,
        message: 'Source Type is required!',
        source: WARNING_SOURCES.EDITOR,
      });
    }
  }
  return errors;
};

export const validateIconControls = (element) => {
  const errors = [];

  if (element.controlType !== SS_CONTROL_TYPE.ICON) {
    return errors;
  }

  if (!element.icon) {
    errors.push({
      instanceId: element.controlId,
      message: 'Icon is required!',
      source: WARNING_SOURCES.EDITOR,
    });
  }

  return errors;
};

export const validateFileViewerControls = (element) => {
  const errors = [];
  if (!element.entityName || element.entityName.length === 0) {
    errors.push({
      instanceId: element.controlId,
      message: 'Entity name is required!',
      source: WARNING_SOURCES.EDITOR,
    });
  }

  if (!element.documentRecordId || element.documentRecordId.length === 0) {
    errors.push({
      instanceId: element.controlId,
      message: 'Document Record ID is required!',
      source: WARNING_SOURCES.EDITOR,
    });
  }

  return errors;
};

export const validateSaveSearchControls = (element) => {
  const warnings = [];
  if (!element.dataLookUpType) {
    warnings.push({
      instanceId: element.controlId,
      message: 'Source Type is required!',
      source: WARNING_SOURCES.EDITOR,
    });
  }

  if (element.dataLookUpType === 'SMARTSOLVEDATA') {
    if (!element.dataObject) {
      warnings.push({
        instanceId: element.controlId,
        message: 'Source Identifier is required!',
        source: WARNING_SOURCES.EDITOR,
      });
    }
    if (!element.searchName) {
      warnings.push({
        instanceId: element.controlId,
        message: 'Search Name is required!',
        source: WARNING_SOURCES.EDITOR,
      });
    }
  }

  return warnings;
};

export const validateAiSummaryControls = (element) => {
  const errors = [];
  if (element.controlType === SS_CONTROL_TYPE.AI_SUMMARY) {
    if (isNilOrEmpty(element.sourceFields)) {
      errors.push({
        instanceId: element.controlId,
        message: 'Source Fields is required!',
        source: WARNING_SOURCES.EDITOR,
      });
    }
    if (
      element.showPostSummaryGenerationMessage &&
      isNilOrEmpty(element.postSummaryGenerationMessage)
    ) {
      errors.push({
        instanceId: element.controlId,
        message: 'Post Summary Generation Message is required!',
        source: WARNING_SOURCES.EDITOR,
      });
    }
  }
  return errors;
};

export const validateAiSimilarRecordsControls = (element) => {
  const errors = [];
  if (element.controlType === SS_CONTROL_TYPE.AI_SIMILAR_RECORDS) {
    if (isNilOrEmpty(element.sourceEntityName)) {
      errors.push({
        instanceId: element.controlId,
        message: 'Source Entity Name is required!',
        source: WARNING_SOURCES.EDITOR,
      });
    }
    if (isNilOrEmpty(element.sourceRecordId)) {
      errors.push({
        instanceId: element.controlId,
        message: 'Source Record Id is required!',
        source: WARNING_SOURCES.EDITOR,
      });
    }
    if (isNilOrEmpty(element.dataObject)) {
      errors.push({
        instanceId: element.controlId,
        message: 'Source Identifier is required!',
        source: WARNING_SOURCES.EDITOR,
      });
    }
    if (isNilOrEmpty(element.searchName)) {
      errors.push({
        instanceId: element.controlId,
        message: 'Saved Search Name is required!',
        source: WARNING_SOURCES.EDITOR,
      });
    }
  }
  return errors;
};

export const validateControls = (formElement) => {
  switch (formElement.controlType) {
    case SS_CONTROL_TYPE.FILE_VIEWER:
      return validateFileViewerControls(formElement);
    case SS_CONTROL_TYPE.SAVED_SEARCH:
      return validateSaveSearchControls(formElement);
    case SS_CONTROL_TYPE.ZOOM:
      return validateZoomControls(formElement);
    case SS_CONTROL_TYPE.ICON:
      return validateIconControls(formElement);
    case SS_CONTROL_TYPE.AI_SUMMARY:
      return validateAiSummaryControls(formElement);
    case SS_CONTROL_TYPE.AI_SIMILAR_RECORDS:
      return validateAiSimilarRecordsControls(formElement);
    default:
      return [];
  }
};

export const validateWarnings = (formModel) => {
  let validationWarnings = [];
  for (const element in formModel) {
    const warnings = validateControls(formModel[element]);
    if (warnings.length > 0) {
      validationWarnings = [...validationWarnings, ...warnings];
    }
  }

  return validationWarnings;
};

export const getFormIdsList = (formModel, formName) => {
  const currentElementKey = Object.keys(formModel).find(
    (key) => formModel[key].formName === formName
  );
  return currentElementKey ? formModel[currentElementKey].childrenNodes : [];
};

export const movedChildrenNodesArray = (childrenNodes, controlId, moveType) => {
  const currentIndex = childrenNodes.indexOf(controlId);
  childrenNodes.splice(currentIndex, 1);

  if (moveType === MOVE_BUTTON_TYPES.UP) {
    childrenNodes.splice(currentIndex - 1, 0, controlId);
    return childrenNodes;
  } else if (moveType === MOVE_BUTTON_TYPES.DOWN) {
    childrenNodes.splice(currentIndex + 1, 0, controlId);
    return childrenNodes;
  }
};

export const getAllNestedChildren = ({ controlId, formModel }) => {
  const childrenIds = formModel[controlId]?.childrenNodes ?? [];

  const nestedChildrenIds = childrenIds.reduce((controlsIds, childId) => {
    const subChildrenIds = getAllNestedChildren({ controlId: childId, formModel });
    return [...controlsIds, ...subChildrenIds];
  }, []);

  return [...childrenIds, ...nestedChildrenIds];
};

export const getMergedZoomCommonProps = (autogeneratedZoom) => {
  const commonProps = _.pick(autogeneratedZoom, [
    'dataObject',
    'dataLookUpType',
    'dataMappingList',
    'filter',
  ]);
  return {
    ...commonProps,
    zoomSource: ZOOM_SOURCE_TYPE.MERGED,
  };
};

export const getDeleteElementParams = ({
  element,
  controlId,
  controlType,
  parent,
  formModel,
  addedElements,
  formInfo,
}) => {
  const elementInAddedArray = addedElements?.find((ele) => ele?.controlId === controlId);
  const controlsIds = [controlId];
  const containerFormName = findParentByType(formModel, controlId, formInfo, 'ssSubForm');
  const formLastModified = getLastModifiedDateContainer(formModel, containerFormName, formInfo);
  const isContainer = CONTAINER_CONTROLS.includes(controlType);
  if (isContainer) {
    const childrenIds = getAllNestedChildren({ controlId, formModel });
    controlsIds.push(...childrenIds);
  }
  const newElement = { ...element, containerFormName, formLastModified };

  const deletedElementParams = {
    element: elementInAddedArray ? element : newElement,
    childElements: elementInAddedArray
      ? element.childrenNodes?.reduce((acc, curr) => {
          acc[curr] = formModel[curr];
          return acc;
        }, {})
      : {},
    parent,
    parentChildrenNodes: formModel[parent]?.childrenNodes ?? [],
    addedElements: elementInAddedArray ? addedElements : undefined,
    deletedControlIds: controlsIds,
  };

  return { newElement, controlsIds, deletedElementParams, elementInAddedArray };
};

export const getAutogeneratedZoomMappings = ({ control = {}, entityResolverRule }) => {
  if (!entityResolverRule) {
    return [];
  }

  const { dataMappingList = [] } = entityResolverRule;
  const { fieldName } = control;
  const resolverField = entityResolverRule.resolver_field;

  const result = dataMappingList.map((mapping) => ({
    id: null,
    updateFrom: mapping.updateFrom,
    updateTo: `${entityResolverRule.tableName}.${mapping.updateTo}`,
    updateToType: mapping.updateToType || null,
    translatedUpdateTo: `${entityResolverRule.tableName}.${mapping.updateTo} (${entityResolverRule.tableName}.${mapping.updateTo})`,
    source: 'resolver',
  }));

  result.push({
    id: null,
    updateFrom: resolverField,
    updateTo: `${entityResolverRule.tableName}.${fieldName}`,
    updateToType: null,
    translatedUpdateTo: `${entityResolverRule.tableName}.${fieldName} (${entityResolverRule.tableName}.${fieldName})`,
    source: 'resolver',
  });

  return result;
};

/**
 * We don't remove controlId from formModel when we delete a control.
 * We only remove controlId from the childrenNodes of its parent.
 */
const isControlDeleted = (formModel, id) => {
  const { parent } = formModel[id];

  if (parent === 'root') {
    return false;
  }

  if (!formModel[parent].childrenNodes?.includes(id)) {
    return true;
  }

  return isControlDeleted(formModel, parent);
};

export const getSearchIds = (formModel, searchText) => {
  if (isNilOrEmpty(searchText)) {
    return [];
  }

  const ids = Object.values(formModel)
    .filter(({ fieldName = '' }) => fieldName.toLowerCase().includes(searchText.toLowerCase()))
    .map(({ controlId }) => controlId);

  const positions = getPositions(formModel);

  ids.sort((a, b) => positions[a] - positions[b]);

  return ids.filter((id) => !isControlDeleted(formModel, id));
};
